import { useMemo } from 'react';
import { X } from 'lucide-react';
import { Badge, Select, type SelectOption } from '../../ui';

export interface MultiPickerProps {
  label?: string;
  /** Helper text under the picker. */
  hint?: string;
  /** All selectable options (value = entity id). */
  options: SelectOption[];
  /** Currently selected ids. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** Placeholder for the "add" select. */
  placeholder?: string;
  /** Resolves an id to a display label for the chips (falls back to the id). */
  labelFor?: (id: string) => string;
}

/**
 * A small, dependency-free multi-select built from the design-system {@link Select}
 * (as an "add" control) plus removable {@link Badge} chips for the current
 * selection. Used across the Batch 4 modals to link plural entity ids
 * (stories / decisions / documents / services / goals / workstreams) without
 * committing to comma-separated id typing. Selected options are hidden from the
 * add list.
 */
export function MultiPicker({
  label,
  hint,
  options,
  value,
  onChange,
  placeholder = 'Add…',
  labelFor,
}: MultiPickerProps) {
  const selected = useMemo(() => new Set(value), [value]);
  const available = useMemo(
    () => options.filter((o) => !selected.has(o.value)),
    [options, selected],
  );

  const add = (id: string) => {
    if (!id || selected.has(id)) return;
    onChange([...value, id]);
  };
  const remove = (id: string) => onChange(value.filter((v) => v !== id));

  const displayLabel = (id: string) =>
    labelFor?.(id) ?? options.find((o) => o.value === id)?.label ?? id;

  return (
    <div className="flex flex-col gap-1.5">
      {label && <span className="text-xs font-medium text-zinc-400">{label}</span>}
      <Select
        placeholder={placeholder}
        value=""
        options={available}
        onChange={(e) => {
          add(e.target.value);
          // Reset back to the placeholder so the same slot can add again.
          e.target.value = '';
        }}
      />
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {value.map((id) => (
            <span key={id} className="inline-flex">
              <Badge tone="neutral">
                <span className="max-w-[14rem] truncate">{displayLabel(id)}</span>
                <button
                  type="button"
                  onClick={() => remove(id)}
                  aria-label={`Remove ${displayLabel(id)}`}
                  className="ml-1 rounded p-0.5 text-zinc-400 transition-colors hover:bg-surface-3 hover:text-red-300"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </Badge>
            </span>
          ))}
        </div>
      )}
      {hint && <p className="text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

export default MultiPicker;
